import React, { useState, useEffect, useRef } from 'react';
import { Truck, Fuel, Camera, CheckCircle, AlertTriangle } from 'lucide-react';
import { getApiUrl } from '@/config';
import type { Vehicle } from '@shared/types';

interface TechnicianVehicleViewProps {
    technicianId: string;
}

export const TechnicianVehicleView: React.FC<TechnicianVehicleViewProps> = ({ technicianId }) => {
    const [vehicle, setVehicle] = useState<Vehicle | null>(null);
    const [loading, setLoading] = useState(true);
    const [showFuelModal, setShowFuelModal] = useState(false);

    // Form State
    const [km, setKm] = useState('');
    const [litres, setLitres] = useState('');
    const [cost, setCost] = useState('');
    const [photo, setPhoto] = useState<string | null>(null); // Base64
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchVehicle();
    }, [technicianId]);

    const fetchVehicle = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl('/api/vehicles'), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const allVehicles: Vehicle[] = await res.json();
            const myVehicle = allVehicles.find(v => v.assignedTechId === technicianId);
            setVehicle(myVehicle || null);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Simple Compression Logic (Max 800px width)
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                const MAX_WIDTH = 800;

                if (width > MAX_WIDTH) {
                    height *= MAX_WIDTH / width;
                    width = MAX_WIDTH;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0, width, height);
                setPhoto(canvas.toDataURL('image/jpeg', 0.7)); // Compress quality 0.7
            };
            img.src = event.target?.result as string;
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!vehicle || !km) return;

        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl(`/api/vehicles/${vehicle._id}/fuel`), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    km: Number(km),
                    litres: Number(litres),
                    cost: Number(cost),
                    proofImage: photo,
                    notes: 'Déclaration Mobile'
                })
            });

            if (res.ok) {
                setShowFuelModal(false);
                fetchVehicle(); // Refresh data
                // Reset form
                setKm(''); setLitres(''); setCost(''); setPhoto(null);
            } else {
                alert('Erreur lors de l\'enregistrement');
            }
        } catch (err) {
            console.error(err);
            alert('Erreur réseau');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Chargement...</div>;

    if (!vehicle) {
        return (
            <div className="flex flex-col items-center justify-center p-8 text-center space-y-4">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center">
                    <Truck size={32} className="text-gray-500" />
                </div>
                <h3 className="text-lg font-medium text-white">Aucun véhicule assigné</h3>
                <p className="text-sm text-gray-400">Demandez à votre responsable de vous assigner un véhicule.</p>
            </div>
        );
    }

    return (
        <div className="p-4 space-y-6">
            {/* Vehicle Card */}
            <div className="glass-panel p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                    <Truck size={120} />
                </div>

                <div className="relative z-10">
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-1">{vehicle.licensePlate}</h2>
                            <p className="text-lg text-primary">{vehicle.brand} {vehicle.model}</p>
                        </div>
                        <div className="bg-emerald-500/10 text-emerald-400 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border border-emerald-500/20">
                            {vehicle.status}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-6">
                        <div className="bg-black/20 p-3 rounded-lg">
                            <p className="text-xs text-gray-400 uppercase mb-1">Kilométrage</p>
                            <p className="text-xl font-mono text-white">{vehicle.currentKm.toLocaleString()} km</p>
                        </div>
                        <div className="bg-black/20 p-3 rounded-lg">
                            <p className="text-xs text-gray-400 uppercase mb-1">Carburant</p>
                            <p className="text-xl text-white">{vehicle.fuelType}</p>
                        </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-white/5">
                        <button
                            onClick={() => {
                                setKm(String(vehicle.currentKm));
                                setShowFuelModal(true);
                            }}
                            className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-xl font-bold shadow-lg shadow-blue-500/20 flex items-center justify-center gap-3 active:scale-95 transition-all"
                        >
                            <Fuel size={24} />
                            Déclarer Plein / Km
                        </button>
                    </div>
                </div>
            </div>

            {/* Fuel Modal */}
            {showFuelModal && (
                <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-4">
                    <div className="bg-[#1e293b] w-full max-w-md rounded-2xl border border-white/10 shadow-2xl flex flex-col max-h-[90vh]">
                        <div className="p-4 border-b border-white/5 flex items-center justify-between">
                            <h3 className="font-bold text-white flex items-center gap-2">
                                <Fuel size={20} className="text-blue-400" />
                                Nouveau Plein
                            </h3>
                            <button onClick={() => setShowFuelModal(false)} className="p-2 text-gray-400 hover:text-white">✕</button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto">

                            {/* KM Input */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-300">Nouveau Kilométrage</label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        value={km}
                                        onChange={e => setKm(e.target.value)}
                                        className="w-full bg-black/20 border border-white/10 rounded-xl p-4 text-2xl font-mono text-white focus:border-blue-500 outline-none"
                                        placeholder={String(vehicle.currentKm)}
                                    />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">km</span>
                                </div>
                                {Number(km) < vehicle.currentKm && (
                                    <p className="text-xs text-amber-500 flex items-center gap-1">
                                        <AlertTriangle size={12} />
                                        Attention: Inférieur au relevé actuel ({vehicle.currentKm})
                                    </p>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-300">Prix Total</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={cost}
                                            onChange={e => setCost(e.target.value)}
                                            step="0.01"
                                            className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-white focus:border-blue-500 outline-none"
                                            placeholder="0.00"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-300">Volume</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={litres}
                                            onChange={e => setLitres(e.target.value)}
                                            step="0.01"
                                            className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-white focus:border-blue-500 outline-none"
                                            placeholder="0.00"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">L</span>
                                    </div>
                                </div>
                            </div>

                            {/* Photo Upload */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-300">Justificatif (Ticket)</label>
                                <div
                                    onClick={() => fileInputRef.current?.click()}
                                    className={`
                                        border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all
                                        ${photo ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-white/10 hover:bg-white/5'}
                                    `}
                                >
                                    {photo ? (
                                        <>
                                            <CheckCircle size={32} className="text-emerald-400" />
                                            <span className="text-xs text-emerald-300">Photo ajoutée !</span>
                                            <img src={photo} alt="Preview" className="h-20 object-contain mt-2 rounded border border-white/10" />
                                        </>
                                    ) : (
                                        <>
                                            <Camera size={32} className="text-gray-400" />
                                            <span className="text-xs text-gray-400">Appuyer pour scanner</span>
                                        </>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        className="hidden"
                                        onChange={handleFileChange}
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={submitting || !km}
                                className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {submitting ? <span className="animate-spin">⌛</span> : <CheckCircle size={20} />}
                                Valider
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
